import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MainLayout } from '../layouts';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import { useSiteName } from '../contexts/SiteConfigContext';

// Create a client for API calls
const client = generateClient();

// GraphQL mutation for verifying email token
const verifyEmailTokenMutation = `
  mutation VerifyEmailToken($token: String!) {
    verifyEmailToken(token: $token)
  }
`;

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get token from URL query parameters
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get('token');
        
        console.log('VerifyEmailPage: Loaded with search params:', location.search);
        console.log('VerifyEmailPage: Token from URL:', token);
        
        if (!token) {
          console.error('VerifyEmailPage: Verification token is missing');
          setError('Verification token is missing. Please check your email link.');
          setIsVerifying(false);
          return;
        }
        
        console.log('VerifyEmailPage: Calling GraphQL mutation to verify token');
        
        // Call the GraphQL mutation to verify the token
        const response: any = await client.graphql({
          query: verifyEmailTokenMutation,
          variables: {
            token
          }
        });
        
        console.log('VerifyEmailPage: GraphQL response:', response);
        
        if (response?.data?.verifyEmailToken) {
          console.log('VerifyEmailPage: Email verification successful');
          setIsSuccess(true);
        } else {
          console.error('VerifyEmailPage: Email verification failed, response data:', response?.data);
          setError('Email verification failed. Please try again or contact support.');
        }
      } catch (err: any) {
        console.error('VerifyEmailPage: Error verifying email:', err);
        setError(err.message || 'Email verification failed. Please try again or contact support.');
      } finally {
        setIsVerifying(false);
      }
    };
    
    verifyEmail();
  }, [location.search]);
  
  // Import useAuth hook
  const { isAuthenticated } = useAuth();
  const siteName = useSiteName();
  
  const handleContinue = () => {
    // Redirect to home page with today's date if already authenticated, otherwise to login page
    if (isAuthenticated) {
      // Format today's date as YYYYMMDD
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const dateString = `${year}${month}${day}`;
      
      console.log(`Email verification successful, redirecting to /${dateString} page`);
      navigate(`/${dateString}`);
    } else {
      navigate('/login', { 
        state: { 
          message: 'Email verified successfully. You can now login.' 
        } 
      });
    }
  };
  
  return (
    <MainLayout>
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">Email Verification</h1>
        
        <div className="mb-6 text-center">
          {isVerifying ? (
            <>
              <div className="flex justify-center mb-4">
                <svg className="animate-spin h-10 w-10 text-racing-blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <p className="text-gray-600">Verifying your email address...</p>
            </>
          ) : isSuccess ? (
            <>
              <svg 
                className="w-16 h-16 mx-auto mb-4 text-green-500" 
                fill="currentColor" 
                viewBox="0 0 20 20" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  fillRule="evenodd" 
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-green-600 font-medium mb-4">Your email has been successfully verified!</p>
              <p className="text-gray-600 mb-6">Thank you for verifying your email address. You can now access all features of {siteName}.</p>
              
              <button
                onClick={handleContinue}
                className="bg-racing-blue text-white py-2 px-4 rounded-lg font-medium hover:bg-racing-blue-dark transition"
              >
                {isAuthenticated ? 'Continue to Home' : 'Continue to Login'}
              </button>
            </>
          ) : (
            <>
              <svg 
                className="w-16 h-16 mx-auto mb-4 text-racing-red" 
                fill="currentColor" 
                viewBox="0 0 20 20" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  fillRule="evenodd" 
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" 
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-racing-red font-medium mb-4">Verification Failed</p>
              <p className="text-gray-600 mb-6">{error}</p>
              
              <button
                onClick={() => {
                  if (isAuthenticated) {
                    // Format today's date as YYYYMMDD
                    const today = new Date();
                    const year = today.getFullYear();
                    const month = String(today.getMonth() + 1).padStart(2, '0');
                    const day = String(today.getDate()).padStart(2, '0');
                    const dateString = `${year}${month}${day}`;
                    
                    console.log(`Redirecting to /${dateString} page`);
                    navigate(`/${dateString}`);
                  } else {
                    navigate('/login');
                  }
                }}
                className="bg-racing-blue text-white py-2 px-4 rounded-lg font-medium hover:bg-racing-blue-dark transition"
              >
                {isAuthenticated ? 'Go to Home' : 'Go to Login'}
              </button>
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default VerifyEmailPage;
